// Fetch interceptor for local dev mock mode.
//
// Security contract:
//   When VITE_MOCK=1 this file replaces window.fetch with a handler dispatcher
//   that ONLY returns synthetic responses for registered (method, pattern)
//   pairs. Any unhandled URL throws a hard error — the original fetch is never
//   restored, so the app literally cannot reach the network (or the RDS behind
//   it) while mock mode is active. The one intentional exception is Vite's own
//   HMR/asset requests which we pass through to the original fetch by matching
//   only same-origin non-API paths.
//
// Registration:
//   handlers.ts supplies the (method, URL-regex → handler) table. Each handler
//   gets the matched Request + captured path params and returns a Response.
//   Handlers must be pure, synchronous-ish, and never call `fetch` themselves.

export interface MockHandlerContext {
  url: URL;
  method: string;
  params: Record<string, string>;
  body: unknown;
  headers: Headers;
}

export type MockHandler = (ctx: MockHandlerContext) => Response | Promise<Response>;

export interface MockRoute {
  method: string;
  /**
   * Regex matched against `url.pathname`. Use named capture groups for params
   * (e.g. `^/cases/(?<caseId>[^/]+)/parties$`).
   */
  pattern: RegExp;
  handler: MockHandler;
}

// Preserve the original fetch for passthrough of Vite internal / same-origin
// asset URLs. DO NOT expose this outside the module — callers must not bypass
// the handler gate.
const originalFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : null;

// Optional artificial latency for mock responses, so skeleton/loading states
// are observable in dev. Set `VITE_MOCK_DELAY_MS=600` (or similar) before
// running `npm run dev`. Default 0 = current synchronous-ish behavior.
const MOCK_DELAY_MS = (() => {
  const raw = import.meta.env.VITE_MOCK_DELAY_MS;
  if (typeof raw !== 'string' || raw.length === 0) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
})();

// Vite's dev-server internal paths. Passthrough these to the real fetch so
// HMR, virtual modules, and source imports still work. Anything else that's
// same-origin goes to the mock dispatcher — which either matches a handler
// or throws (preserving the "no unintended network" guarantee).
const VITE_INTERNAL_PREFIXES = ['/@vite/', '/@id/', '/@fs/', '/@react-refresh', '/src/', '/node_modules/'];
const STATIC_ASSET_EXT = /\.(?:css|js|mjs|map|svg|png|jpg|jpeg|gif|webp|ico|woff2?|ttf|otf|eot|json|txt|wasm)$/i;

function isLikelyLocalAsset(url: URL): boolean {
  if (typeof window === 'undefined') return false;
  if (url.origin !== window.location.origin) return false;
  const p = url.pathname;
  if (VITE_INTERNAL_PREFIXES.some(prefix => p.startsWith(prefix))) return true;
  if (STATIC_ASSET_EXT.test(p)) return true;
  return false;
}

function jsonResponse(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
}

function errorResponse(status: number, error: string): Response {
  return new Response(JSON.stringify({ error }), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

export const mockResponse = { json: jsonResponse, error: errorResponse };

function normalizeInput(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

async function parseBody(req: Request | RequestInit | null): Promise<unknown> {
  if (!req) return undefined;
  // A Request object consumes its body stream, so we clone.
  if (req instanceof Request) {
    const ct = req.headers.get('Content-Type') ?? '';
    if (!ct.includes('application/json')) return undefined;
    try {
      return await req.clone().json();
    } catch {
      return undefined;
    }
  }
  const init = req as RequestInit;
  if (init.body == null) return undefined;
  if (typeof init.body === 'string') {
    try {
      return JSON.parse(init.body);
    } catch {
      return init.body;
    }
  }
  return undefined;
}

export function installFetchInterceptor(routes: MockRoute[]): void {
  if (!originalFetch) {
    throw new Error('installFetchInterceptor: no window.fetch to replace');
  }

  const dispatcher = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const rawUrl = normalizeInput(input);
    // Resolve relative URLs against current origin.
    const url = new URL(rawUrl, window.location.origin);

    // Pass-through for same-origin non-API requests (Vite HMR, /src/*, assets).
    if (isLikelyLocalAsset(url)) {
      return originalFetch(input, init);
    }

    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const reqForBody = input instanceof Request ? input : null;
    const body = await parseBody(reqForBody ?? (init ?? null));

    for (const route of routes) {
      if (route.method !== method) continue;
      const m = url.pathname.match(route.pattern);
      if (!m) continue;
      const params = { ...(m.groups ?? {}) } as Record<string, string>;
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : {}));
      try {
        if (MOCK_DELAY_MS > 0) {
          await new Promise(resolve => setTimeout(resolve, MOCK_DELAY_MS));
        }
        return await route.handler({ url, method, params, body, headers });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(`[devMock] handler threw for ${method} ${url.pathname}:`, err);
        return errorResponse(500, `mock handler error: ${msg}`);
      }
    }

    // Hard block: no registered handler AND the URL looks like an API call.
    // Do NOT fall through to the real network — that's the whole security
    // property of mock mode.
    const msg = `[devMock] unhandled ${method} ${url.pathname} — no mock registered; refusing to hit network.`;
    console.error(msg);
    throw new Error(msg);
  };

  // Permanent replacement. No teardown API — mock mode is app-lifetime.
  window.fetch = dispatcher as typeof window.fetch;
}
